import { gql, useQuery } from '@apollo/client';
import { useShareTokenJwt } from '@app/hooks/useShareTokenJwt';
import { QuestionnaireWebsiteScan } from './useSubmitWebsiteUrlMutation';

const QUESTIONNAIRE_WEBSITE_SCAN = gql`
  query QuestionnaireWebsiteScan($token: String) {
    questionnaireWebsiteScan(token: $token) {
      url
      propertyId
      status
      scanFinished
      progressCurrent
      progressTotal
    }
  }
`;

export interface QuestionnaireWebsiteScanData {
  questionnaireWebsiteScan: QuestionnaireWebsiteScan | null;
}

export interface QuestionnaireWebsiteScanVariables {
  token?: string | null;
}

/**
 * Reads the questionnaire's embedded website-scan status (URL, progress,
 * completion). Polls while the scan is still running so the progress chip
 * updates live. Polling stops automatically once the scan is finished.
 */
export function useQuestionnaireWebsiteScanQuery(pollIntervalMs = 4000, skip = false) {
  const { token, waitingForShareToken } = useShareTokenJwt();
  const result = useQuery<QuestionnaireWebsiteScanData, QuestionnaireWebsiteScanVariables>(QUESTIONNAIRE_WEBSITE_SCAN, {
    variables: { token },
    fetchPolicy: 'cache-and-network',
    pollInterval: pollIntervalMs,
    skip: skip || waitingForShareToken,
  });

  const scan = result.data?.questionnaireWebsiteScan ?? null;
  // Stop polling once the scan has reached a terminal state.
  if (scan && (scan.scanFinished || scan.status === 'completed' || scan.status === 'failed')) {
    result.stopPolling();
  }

  return result;
}
